import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Collapse, IconButton, List, ListItemButton, ListItemText } from '@mui/material';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import type { MusicAlbumRow } from '../../api/music';

/** Selection value: null = all, NO_ALBUM = tracks without album_path, otherwise an album_path. */
export const NO_ALBUM = '\u0000none';

export interface AlbumNode {
    name: string;
    path: string;
    totalCount: number;
    totalSize: number;
    children: AlbumNode[];
}

export interface AlbumTreeData {
    roots: AlbumNode[];
    nodes: Map<string, AlbumNode>;
    totalCount: number;
    noAlbumCount: number;
}

export const buildAlbumTree = (rows: MusicAlbumRow[]): AlbumTreeData => {
    const nodes = new Map<string, AlbumNode>();
    const roots: AlbumNode[] = [];
    let totalCount = 0;
    let noAlbumCount = 0;
    for (const row of rows) {
        totalCount += row.track_count;
        const parts = (row.album_path ?? '').split('/').map((part) => part.trim()).filter(Boolean);
        if (parts.length === 0) {
            noAlbumCount += row.track_count;
            continue;
        }
        let path = '';
        let siblings = roots;
        for (const part of parts) {
            path = path ? `${path}/${part}` : part;
            let node = nodes.get(path);
            if (!node) {
                node = { name: part, path, totalCount: 0, totalSize: 0, children: [] };
                nodes.set(path, node);
                siblings.push(node);
            }
            node.totalCount += row.track_count;
            node.totalSize += row.total_size_bytes;
            siblings = node.children;
        }
    }
    const sort = (list: AlbumNode[]) => {
        list.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true, sensitivity: 'base' }));
        list.forEach((node) => sort(node.children));
    };
    sort(roots);
    return { roots, nodes, totalCount, noAlbumCount };
};

interface AlbumTreeProps {
    rows: MusicAlbumRow[];
    selected: string | null;
    onSelect: (value: string | null) => void;
}

export const AlbumTree = ({ rows, selected, onSelect }: AlbumTreeProps) => {
    const { t } = useTranslation();
    const tree = useMemo(() => buildAlbumTree(rows), [rows]);
    const [expanded, setExpanded] = useState<Set<string>>(new Set());

    const toggle = (path: string) => setExpanded((prev) => {
        const next = new Set(prev);
        if (next.has(path)) next.delete(path); else next.add(path);
        return next;
    });

    const renderNode = (node: AlbumNode, depth: number) => {
        const hasChildren = node.children.length > 0;
        const isOpen = expanded.has(node.path);
        return (
            <Box key={node.path}>
                <ListItemButton
                    selected={selected === node.path}
                    onClick={() => onSelect(node.path)}
                    sx={{ pl: 1 + depth * 2 }}
                >
                    {hasChildren ? (
                        <IconButton
                            size="small"
                            edge="start"
                            onClick={(event) => {
                                event.stopPropagation();
                                toggle(node.path);
                            }}
                        >
                            {isOpen ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
                        </IconButton>
                    ) : (
                        <Box sx={{ width: 30, flexShrink: 0 }} />
                    )}
                    <ListItemText
                        primary={node.name}
                        secondary={`${node.totalCount}`}
                        primaryTypographyProps={{ noWrap: true }}
                        title={node.path}
                    />
                </ListItemButton>
                {hasChildren && (
                    <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        {node.children.map((child) => renderNode(child, depth + 1))}
                    </Collapse>
                )}
            </Box>
        );
    };

    return (
        <List dense disablePadding sx={{ maxHeight: 420, overflowY: 'auto' }}>
            <ListItemButton selected={selected === null} onClick={() => onSelect(null)}>
                <ListItemText primary={t('music.all')} secondary={`${tree.totalCount}`} />
            </ListItemButton>
            {tree.roots.map((node) => renderNode(node, 0))}
            {tree.noAlbumCount > 0 && (
                <ListItemButton selected={selected === NO_ALBUM} onClick={() => onSelect(NO_ALBUM)}>
                    <ListItemText primary={t('music.noAlbum')} secondary={`${tree.noAlbumCount}`} />
                </ListItemButton>
            )}
        </List>
    );
};
